import { Icon } from '@design/components/icon/Icon';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { capitalizeFirstLetter } from '@helpers/utils/strings/strings';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { NoDataFound } from '@terminal/components/no-data-found/NoDataFound';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { TabsType } from '@terminal/modules/tab-content/types';
import { usePulseUpcomingEarnings } from '@terminal/react-query-hooks/themes/use-pulse-table/usePulseTables';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { PulseSection } from '@terminal/views/theme-overview/tab-content/pulse-overview/components/pulse-section/PulseSection';
import { PulseRankedTable } from '@terminal/views/theme-overview/tab-content/pulse-overview/components/pulse-tables/PulseRankedTable';
import tableStyles from '@terminal/views/theme-overview/tab-content/pulse-overview/components/pulse-tables/PulseTables.module.scss';
import { getReportingTimeIcon } from '@terminal/widgets/earnings/utils/utils';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import styles from './UpcomingEarnings.module.scss';

export interface UpcomingEarningsProps {
  themeId: string;
}

export const UpcomingEarnings = ({ themeId }: UpcomingEarningsProps) => {
  const { t } = useTranslation(['widget']);
  const { navigateToPage } = useNavigateToAssetOverview();
  const upcomingEarnings = usePulseUpcomingEarnings({ themeId });

  const openAssetOverview = (entityTag: string) => {
    navigateToPage({
      entityTag,
      source: POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER,
      tab: TabsType.Earnings,
    });
  };

  /* i18next-extract-mark-context-next-line ["notScheduledYet", "remainingDatesUnannounced", "reportingComplete"] */
  const upcomingEarningsStatus = upcomingEarnings.status
    ? t('widget:themeOverview.pulse.earningsStatus', {
        context: upcomingEarnings.status.key,
      })
    : t('widget:themeOverview.pulse.upcomingEarningsEmpty');

  const earnings = upcomingEarnings.data;
  const isEmpty = !earnings?.length;
  const title = t('widget:themeOverview.pulse.upcomingEarnings');

  return (
    <ErrorBoundaryWrapper showBorder={false}>
      <PulseSection
        title={capitalizeFirstLetter(title.toLowerCase())}
        className={styles.root}
        dataTestId="industry-drawer-upcoming-earnings"
        flush
      >
        {upcomingEarnings.isLoading ? (
          <SkeletonLoader
            areas={[
              {
                styles: { width: '100%', height: '172px', borderRadius: '8px' },
              },
            ]}
          />
        ) : isEmpty ? (
          <NoDataFound
            bannerPath="/app/static/images/empty-state/radio-telescope.png"
            className={styles.placeholder}
            size="small"
            title={upcomingEarningsStatus}
          />
        ) : (
          <PulseRankedTable
            className={styles.table}
            rows={earnings}
            lastColumnLabel={t('widget:themeOverview.pulse.scheduledDate')}
            lastColumnAlignment="left"
            data-testid="industry-drawer-upcoming-earnings-table"
            onRowClick={earning => openAssetOverview(earning.company.entityTag)}
            renderLastCell={earning => {
              const scheduledDate = parseISO(
                earning.earningsItem.reporting_date
              );

              return (
                <span className={tableStyles.scheduledDate}>
                  {format(scheduledDate, 'yyyy-MM-dd')}
                  <Icon
                    iconName={getReportingTimeIcon(scheduledDate)}
                    size={14}
                    fillColor="var(--icon-warning)"
                  />
                </span>
              );
            }}
          />
        )}
      </PulseSection>
    </ErrorBoundaryWrapper>
  );
};
